// ./src/components/features/Connectors/ConfigEditors/AzureEventGridConnectorConfigEditor.tsx
import React, { useState, useEffect, useCallback } from "react";
import { Stack, TextInput, Text, Anchor, Alert, Code } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { stringifyObject, parseObject } from "@/utils";
import { AzureEventGridConnectorConfig } from "@/types";

export interface AzureEventGridConnectorConfigEditorProps {
  /** JSON string of connector configuration */
  value: string;

  /** Callback when configuration changes */
  onChange: (value: string) => void;

  /** Whether the editor is disabled */
  disabled?: boolean;
}

// Default empty configuration
const DEFAULT_CONFIG: AzureEventGridConnectorConfig = {
  topicEndpoint: "",
  accessKey: "",
  eventType: "FormSubmission",
  subject: "/forms/{{formId}}/submissions/{{requestId}}",
  dataVersion: "1.0",
};

/**
 * Parse and validate Azure Event Grid connector configuration
 */
const parseAzureEventGridConfig = (
  jsonString: string
): AzureEventGridConnectorConfig => {
  const parsed = parseObject(jsonString, DEFAULT_CONFIG);
  return parsed;
};

export const AzureEventGridConnectorConfigEditor: React.FC<
  AzureEventGridConnectorConfigEditorProps
> = ({ value, onChange, disabled = false }) => {
  // Parse incoming value to config state
  const [config, setConfig] = useState<AzureEventGridConnectorConfig>(() =>
    parseAzureEventGridConfig(value)
  );

  // Sync config when external value changes
  useEffect(() => {
    const parsed = parseAzureEventGridConfig(value);
    setConfig(parsed);
  }, [value]);

  // Update parent when config changes
  const updateConfig = useCallback(
    (newConfig: AzureEventGridConnectorConfig) => {
      setConfig(newConfig);
      onChange(stringifyObject(newConfig, DEFAULT_CONFIG));
    },
    [onChange]
  );

  // Handle topic endpoint change
  const handleTopicEndpointChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, topicEndpoint: e.target.value });
  };

  // Handle access key change
  const handleAccessKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, accessKey: e.target.value });
  };

  // Handle event type change
  const handleEventTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, eventType: e.target.value });
  };

  // Handle subject change
  const handleSubjectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, subject: e.target.value });
  };

  // Handle data version change
  const handleDataVersionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, dataVersion: e.target.value });
  };

  // Validate topic endpoint URL
  const topicEndpointError = (() => {
    if (!config.topicEndpoint) return undefined;
    try {
      new URL(config.topicEndpoint);
      return undefined;
    } catch {
      return "Must be a valid URL";
    }
  })();

  return (
    <Stack gap="md">
      {/* Help Alert */}
      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        <Text size="sm">
          Find your topic endpoint and access key in{" "}
          <Anchor
            href="https://portal.azure.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            Azure Portal
          </Anchor>
          {" → Event Grid Topic → Overview (endpoint) & Access keys"}
        </Text>
      </Alert>

      {/* Topic Endpoint Field */}
      <TextInput
        label="Topic Endpoint"
        placeholder="https://your-topic.eastus-1.eventgrid.azure.net/api/events"
        description="Event Grid topic endpoint URL"
        value={config.topicEndpoint}
        onChange={handleTopicEndpointChange}
        disabled={disabled}
        required
        size="md"
        error={topicEndpointError}
      />

      {/* Access Key Field */}
      <TextInput
        label="Access Key"
        placeholder="aBcDeFgHiJkLmNoPqRsTuVwXyZ..."
        description="Topic access key (Key 1 or Key 2)"
        value={config.accessKey}
        onChange={handleAccessKeyChange}
        disabled={disabled}
        required
        size="md"
        type="password"
        autoComplete="off"
      />

      {/* Event Type Field */}
      <TextInput
        label="Event Type"
        placeholder="FormSubmission"
        description="Event type used for filtering in subscriptions"
        value={config.eventType || "FormSubmission"}
        onChange={handleEventTypeChange}
        disabled={disabled}
        size="md"
      />

      {/* Subject Field */}
      <TextInput
        label="Subject Pattern"
        placeholder="/forms/{{formId}}/submissions/{{requestId}}"
        description="Event subject used for filtering in subscriptions"
        value={config.subject || "/forms/{{formId}}/submissions/{{requestId}}"}
        onChange={handleSubjectChange}
        disabled={disabled}
        size="md"
      />

      {/* Data Version Field */}
      <TextInput
        label="Data Version"
        placeholder="1.0"
        description="Version of the event data schema"
        value={config.dataVersion || "1.0"}
        onChange={handleDataVersionChange}
        disabled={disabled}
        size="md"
      />

      {/* Placeholders Help */}
      <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
        <Text size="sm" fw={500} mb={4}>
          Available placeholders for Subject:
        </Text>
        <Text size="xs" component="div">
          <Code>{"{{requestId}}"}</Code> — Submission ID (e.g.,
          20250530143022-ABCD)
          <br />
          <Code>{"{{formId}}"}</Code> — Form UUID
          <br />
          <Code>{"{{formName}}"}</Code> — Form name (sanitized)
          <br />
          <Code>{"{{timestamp}}"}</Code> — Unix timestamp
          <br />
          <Code>{"{{date}}"}</Code> — Date (YYYY-MM-DD)
          <br />
          <Code>{"{{randUuid}}"}</Code> — Random UUID
        </Text>
      </Alert>
    </Stack>
  );
};
